import type { FC } from 'react';

import { Text } from '@/core/tamagoshi/Text/Text';
import { useBooleanFlags } from '@/domains/core/flags/flags.hooks';
import { isB2B } from '@/domains/core/settings/utils';
import { H3 } from '@/domains/core/tamagoshiv2/components/Typography/Typography';
import type { Review } from '@/domains/productDiscovery/ArticleReviews/ArticleReviews.model';
import { ReviewCard } from '@/domains/productDiscovery/ArticleReviews/components/ReviewCard/ReviewCard';
import { useMainReviews } from '@/domains/productDiscovery/ArticleReviews/hooks/useMainReviews/useMainReviews';
import {
  customerReviews,
  reviewsSeeAll,
} from '@/domains/productDiscovery/ArticleReviews/translations';
import { ViewMoreButton } from '@/domains/productDiscovery/ProductPage/b2c/components/ViewMoreButton/ViewMoreButton';
import { StringAbTests } from '@/domains/productDiscovery/ProductPage/constants';
import { useSidePanel } from '@/domains/productDiscovery/ProductPage/hooks/useSidePanel';
import { useTabs } from '@/domains/productDiscovery/ProductPage/hooks/useTabs';

import styles from './reviews.module.scss';

export const Reviews: FC = () => {
  const { reviews, total, hasMoreReviews, subtitle } = useMainReviews();
  const [isProductPageRevampEnabled] = useBooleanFlags([
    StringAbTests.ENABLE_PRODUCT_PAGE_REVAMP,
  ]);
  const { goToReviews: openReviewsTab } = useTabs();

  const { goToReview } = useSidePanel();

  if (reviews.length === 0) return null;

  return (
    <div className={styles.container} data-testid="Reviews">
      <header className={styles.headerContainer}>
        <div className={styles.header}>
          <H3 variant="h4">{customerReviews}</H3>
        </div>
        <Text type={isB2B() ? 'body2' : 'small'} color="#505971">
          {subtitle}
        </Text>
      </header>
      <div className={styles.cardsContainer}>
        {reviews.map((review: Review) => (
          <ReviewCard
            {...review}
            key={review.reviewId}
            hideTranslatedTag
            hidePictures
          />
        ))}
      </div>
      {hasMoreReviews && (
        <ViewMoreButton
          text={`${reviewsSeeAll} (${total})`}
          onClick={() =>
            isProductPageRevampEnabled ? goToReview() : openReviewsTab()
          }
        />
      )}
    </div>
  );
};

Reviews.displayName = 'Reviews';
